/**
 * Paints a STRIDE tall but lays out a ROW-HEIGHT content box.
 *
 * The old sidebar was one continuous `elevation-1` column with `gap: 0.4rem` between its cards
 * (`gantt-chart/components/sidebar.tsx:22-33`), so the space between rows read as panel
 * background. Here each row owns its own sidebar cell, so a cell only as tall as
 * `--gantt-row-height` leaves `--gantt-row-gap` unpainted and `.canvas`'s day grid — weekend
 * stripes, dashes, week lines — stripes straight across the column.
 *
 * Spending the gap as bottom padding inside a border-box of one full stride paints it in
 * `--gantt-sidebar-bg` while leaving the content box at `--gantt-row-height`, which is what
 * `.track` also resolves to. `align-self: flex-start` is load-bearing: `.row` centres its
 * children, and a stride-tall cell centred in a row-height line box would sit 2px high and take
 * the card with it, misaligning every sidebar row against its own bar.
 */
.header {
  position: sticky;
  left: 0;
  /* Above the bars (4) and their resize strips / off-screen chips (5), below the timeline header
     group (11). The old sidebar sat at 10 over strips at 4-5 for the same reason: a bar scrolled
     behind the sticky column must not paint on top of it. */
  z-index: 10;
  flex: 0 0 var(--gantt-sidebar-width);
  width: var(--gantt-sidebar-width);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  align-self: flex-start;
  gap: 0.8rem;
  height: var(--gantt-row-stride);
  padding-block-end: var(--gantt-row-gap);
  padding-inline: 0.8rem;
  /* Depth used to come from nested containers; now it is one declaration. */
  padding-inline-start: calc(0.8rem + var(--row-depth) * 1.6rem);
  background-color: var(--gantt-sidebar-bg);
  min-width: 0;
}

.chevron {
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  flex: 0 0 2.4rem;
  cursor: pointer;
  color: var(--color-grayscale-manatee);
  transition: transform 0.15s ease;

  &[data-collapsed='false'] {
    transform: rotate(0deg);
  }

  &[data-collapsed='true'] {
    transform: rotate(-90deg);
  }
}

.name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-texts-high-contrast);
  font-size: 1.6rem;
}
